import React, { useEffect } from "react";
import { Input, DropdownOption, Dropdown } from "@abb/abb-common-ux-react";
import styled from "styled-components";
import { FormikProps, withFormik } from "formik";
import * as Yup from "yup";
import { handleFormValueChange, isFormFieldValid } from "../../../../utilities/formikUtils";
import { RequestState } from "../../../../framework/state/requestState";
import { RequestStatus } from "../../../../framework/state/requestStatus";
import { ContactSalesSupportReasonDto, ContactSalesSupportReasons } from "../../../../api";
import { useWindowIsMaxHeight } from "../../../../framework/hooks/useWindowIsMaxHeight";
import { Button } from "framework/components/Button";
import { useTranslation } from "react-i18next";
import { Dialog } from "framework/components/Dialog";

export interface ContactSalesSupportDialogProps {
  isOpen: boolean;
  reasons: ContactSalesSupportReasonDto[];
  sendSupportRequest: RequestState<void>;
  onClose: () => void;
  onSendSupportRequest: (fields: ContactSalesSupportFields) => void;
}

const FormRow = styled.div`
  margin-block: ${(props) => props.theme.sizes.sm};
`;

const FormContainer = styled.div`
  margin-block: ${(props) => props.theme.sizes.sm};
  display: flex;
  flex-direction: column;
  textarea {
    min-height: 200px !important;
    min-width: 400px !important;
  }
`;

export interface ContactSalesSupportFields {
  description: string;
  reasonId: ContactSalesSupportReasons | undefined;
}

const initialFields: ContactSalesSupportFields = {
  description: "",
  reasonId: undefined
};

const Inner: React.FunctionComponent<
  ContactSalesSupportDialogProps & FormikProps<ContactSalesSupportFields>
> = (props) => {
  const sendSupportRequestStatus = props.sendSupportRequest.status;
  useEffect(() => {
    if (sendSupportRequestStatus === RequestStatus.Completed) {
      props.resetForm();
      props.onClose();
    }
  }, [sendSupportRequestStatus, props]);

  const isOverflowVisible = useWindowIsMaxHeight(600);
  const { t } = useTranslation();

  return (
    <Dialog
      title={t("Contact order handling")}
      isOpen={props.isOpen}
      closeOnEscape
      onClose={props.onClose}
      contentClassName={
        !isOverflowVisible ? "dialog-content overflow-auto" : "dialog-content overflow-visible"
      }
    >
      <FormContainer>
        <FormRow>
          <Input
            label={t("Description")}
            dataType="textarea"
            value={props.values.description}
            showValidationIconWhenInvalid
            validator={() => isFormFieldValid(props, "description")}
            onValueChange={(e: string) => {
              handleFormValueChange(props, "description", e);
            }}
          />
        </FormRow>
        <FormRow>
          <Dropdown
            monochrome
            label={t("Reason")}
            clearable
            value={[
              {
                value: props.values.reasonId,
                label:
                  props.reasons.find((r) => r.reasonId?.toString() === props.values.reasonId)
                    ?.description ?? ""
              }
            ]}
            onChange={(
              selection: Array<{
                value: any;
                label: string;
                isNew: boolean;
              }>
            ) => {
              handleFormValueChange(
                props,
                "reasonId",
                selection.length > 0 ? selection[0].value : ""
              );
            }}
          >
            {props.reasons.map((reason) => (
              <DropdownOption
                key={reason.reasonId}
                value={reason.reasonId?.toString()}
                label={reason.description ?? ""}
              />
            ))}
          </Dropdown>
        </FormRow>
        <FormRow style={{ marginLeft: "auto" }}>
          <Button
            text={t("Send")}
            sizeClass="medium"
            buttonType="primary"
            isLoading={sendSupportRequestStatus === RequestStatus.Pending}
            disabled={!props.isValid}
            onClick={props.submitForm}
          />
        </FormRow>
      </FormContainer>
    </Dialog>
  );
};

const ContactSalesSupportSchema = Yup.object().shape({
  description: Yup.string().min(5, "Provide a description").required(),
  reasonId: Yup.string().required("Provide a reason")
});

const ContactSalesSupportDialog = withFormik<
  ContactSalesSupportDialogProps,
  ContactSalesSupportFields
>({
  mapPropsToValues: () => {
    return initialFields;
  },
  validateOnMount: true,
  enableReinitialize: true,
  validationSchema: ContactSalesSupportSchema,
  handleSubmit: (values, props) => {
    props.props.onSendSupportRequest(values);
  }
})(Inner);

export default ContactSalesSupportDialog;
